import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import get from 'lodash.get';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Divider,
  Typography,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Switch,
  Button,
  Tooltip
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { NearMeOutlined } from '@material-ui/icons';
import PermissionCheck from 'PermissionsCheck.js';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2)
  },
  item: {
    display: 'flex',
    flexDirection: 'column'
  },
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.5rem',
    width: '70%'
  },
  switch: {
    width: 'max-content',
    margin: 0,
    '& .MuiFormControlLabel-label	': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      width: '100px'
    }
  },
  schemaButton: {
    marginTop: '20px',
    marginBottom: '4px',
    marginLeft: '8px'
  }
}));

const ProductSettings = props => {
  const { className, useMetaDataSettings, productSettingsContent, ...rest } = props;
  const permissions = PermissionCheck();
  const { hasPermission, isAdminUser } = permissions;

  const {
    PRODUCT_INTEGRATIONS,
    products,
    handleChange,
    settingsMetaData,
    isSettingsMetaDataUpdating,
    isLoading,
    onBlur,
    onFocus,
    getErrorMessage
  } = useMetaDataSettings;

  const settingsProductIntegrations = get(
    settingsMetaData,
    PRODUCT_INTEGRATIONS,
    null
  );

  const productIntegrations = settingsProductIntegrations || [];

  const classes = useStyles();
  const history = useHistory();
  const [sortedProducts, setSortedProducts] = useState([]);

  useEffect(() => {
    //LMS will not be added in env variables
    let currentProducts = [...sortedProducts];
    let hasChanged = false;
    if (products.length > 0) {
      products.map(item => {
        if (!currentProducts.some(p => p.id === item)) {
          hasChanged = true;
          let obj = {
            id: item,
            label: item.split(':')[1],
            checked: false
          };
          currentProducts.push(obj);
        }
      });
    }

    if (productIntegrations.length > 0) {
      hasChanged = true;
      currentProducts.forEach(item => {
        item.checked = productIntegrations.includes(item.id) ? true : false;
        return item;
      });
    }
    hasChanged && setSortedProducts(currentProducts);
  }, [products, productIntegrations]);

  const handleProductSelection = (e, i) => {
    let currentProducts = [...sortedProducts];
    currentProducts[i].checked = e.target.checked;
    setSortedProducts(currentProducts);

    let selected = currentProducts
      .filter(item => item.checked)
      .map(item => item.id);
    handleChange({ target: { name: PRODUCT_INTEGRATIONS, value: selected } });
  };

  const SkeletonCardContent = () => {
    return (
      <CardContent>
        <Grid item md={6} xs={12}>
          <Typography display="block" style={{ fontSize: '2.2rem' }}>
            <Skeleton />
          </Typography>
        </Grid>
      </CardContent>
    );
  };

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <form>
        <CardHeader
          subheader={productSettingsContent?.description || `Manage product settings`}
          title={productSettingsContent?.title || `Product Integration`}
        />
        <Divider />
        {isLoading ||
        isSettingsMetaDataUpdating ||
        sortedProducts.length === 0 ? (
          <SkeletonCardContent />
        ) : (
          <CardContent>
            <Grid item xs={12}>
              <FormGroup row className={classes.container}>
                {sortedProducts.map((p, i) => (
                  <Tooltip title={p.label}>
                    <FormControlLabel
                      className={classes.switch}
                      value={p.id}
                      label={p.label}
                      key={i}
                      control={
                        <Switch
                          checked={p.checked || false}
                          onChange={e => {
                            handleProductSelection(e, i);
                          }}
                          onFocus={onFocus}
                          onBlur={onBlur}
                          key={p}
                          color="primary"
                          inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                      }
                    />
                  </Tooltip>
                ))}
                <FormHelperText>{getErrorMessage('type')}</FormHelperText>
              </FormGroup>
            </Grid>
            {hasPermission('Rguest_writeSchema') && isAdminUser() && (
              <Grid item className={classes.schemaButton}>
                <Tooltip title={productSettingsContent?.schema?.schemaConfigurationTooltip || `Click on this button to configure schema for the enabled and saved products.`}>
                  <span>
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      startIcon={<NearMeOutlined fontSize="default" />}
                      onClick={() => history.push('/schema')}>
                      {productSettingsContent?.schema?.schemaConfigurationButton || `Schema configuration`}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={productSettingsContent?.enrichedSchema?.enrichedSchemaConfigurationTooltip || `Click on this button to configure enriched schema for the enabled and saved products.`}>
                  <span>
                    <Button
                      style={{ marginLeft: '16px' }}
                      size="small"
                      color="primary"
                      variant="contained"
                      startIcon={<NearMeOutlined fontSize="default" />}
                      onClick={() => history.push('/enriched-schema')}>
                      {productSettingsContent?.enrichedSchema?.enrichedSchemaConfigurationButton || `Enriched Schema configuration`}
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            )}
          </CardContent>
        )}
      </form>
    </Card>
  );
};

ProductSettings.propTypes = {
  className: PropTypes.string,
  useDeviceContextSettings: PropTypes.object
};

export default ProductSettings;
